import { isApiError } from "@pokifolio/api-client";
import {
  GRADING_COMPANIES,
  type PortfolioItemInput,
  PortfolioItemInputSchema,
  RAW_CONDITIONS,
} from "@pokifolio/contracts";
import { type SubmitEvent, useId, useState } from "react";
import type { z } from "zod";

import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import { Input, Select, Textarea } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { GRADING_COMPANY_LABELS, RAW_CONDITION_LABELS } from "../research/format";
import type { CardFormValues } from "./card-values";

const CURRENCIES = ["USD", "GBP", "EUR", "CAD", "AUD", "JPY", "PKR"] as const;

/** What is sent; a price's currency is only sent with a price. */
function toInput(v: CardFormValues): Record<string, unknown> {
  const blank = (s: string) => (s.trim() === "" ? null : s);
  return {
    name: v.name,
    setName: v.setName,
    cardNumber: v.cardNumber,
    language: v.language,
    cardType: v.cardType,
    condition: blank(v.condition),
    gradingCompany: blank(v.gradingCompany),
    grade: v.grade.trim() === "" ? null : Number(v.grade),
    certificateNumber: v.certificateNumber,
    quantity: v.quantity.trim() === "" ? undefined : Number(v.quantity),
    purchasePrice: v.purchasePrice,
    purchaseCurrency: v.purchasePrice.trim() === "" ? null : v.purchaseCurrency,
    purchaseDate: v.purchaseDate,
    boughtFrom: v.boughtFrom,
    storageLocation: v.storageLocation,
    notes: v.notes,
    researchId: v.researchId,
  };
}

interface PortfolioFormProps {
  readonly initial: CardFormValues;
  /** True when this person cannot see the price that was entered (it is kept as it is). */
  readonly costHidden?: boolean;
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onSubmit: (input: z.output<typeof PortfolioItemInputSchema>) => void;
  readonly onCancel?: () => void;
}

/** The card's details, checked with the same rules as the server before anything is sent. */
export function PortfolioForm({
  initial,
  costHidden = false,
  submitLabel,
  pending,
  error,
  onSubmit,
  onCancel,
}: PortfolioFormProps) {
  const [values, setValues] = useState<CardFormValues>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const id = useId();
  const set = <K extends keyof CardFormValues>(key: K, value: CardFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const errorFor = (key: string) => errors[key] ?? serverFieldError(error, key);
  const fieldErrorShown =
    Object.keys(errors).length > 0 || (isApiError(error) && error.fieldErrors.length > 0);
  const text = (
    key: keyof CardFormValues,
    label: string,
    extra: { hint?: string; placeholder?: string } = {},
  ) => (
    <Field
      id={`${id}-${key}`}
      label={label}
      error={errorFor(key)}
      {...(extra.hint === undefined ? {} : { hint: extra.hint })}
    >
      <Input
        value={values[key] ?? ""}
        onChange={(e) => set(key, e.target.value)}
        {...(extra.placeholder === undefined ? {} : { placeholder: extra.placeholder })}
      />
    </Field>
  );

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const input = toInput(values);
    if (costHidden) {
      // The price is not shown to this person: keep what was entered by not sending a change to it.
      delete input.purchasePrice;
      delete input.purchaseCurrency;
    }
    const result = validate(PortfolioItemInputSchema, input as PortfolioItemInput);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    onSubmit(result.data);
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        {text("name", "Card name", { placeholder: "e.g. Venusaur EX" })}
        {text("setName", "Set (optional)", { placeholder: "e.g. XY Black Star Promos" })}
        {text("cardNumber", "Card number (optional)", { placeholder: "e.g. XY28 or 4/102" })}
        {text("language", "Language (optional)", { placeholder: "e.g. English" })}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-foreground">Card type</legend>
        <div className="flex gap-5">
          {(["raw", "graded"] as const).map((type) => (
            <label key={type} className="inline-flex items-center gap-1.5 text-sm text-foreground">
              <input
                type="radio"
                name={`${id}-cardType`}
                checked={values.cardType === type}
                onChange={() => set("cardType", type)}
                className="size-4 accent-primary"
              />
              {type === "raw" ? "Raw (not graded)" : "Graded"}
            </label>
          ))}
        </div>
      </fieldset>

      {values.cardType === "raw" ? (
        <Field id={`${id}-condition`} label="Condition (optional)" error={errorFor("condition")}>
          <Select
            value={values.condition}
            onChange={(e) => set("condition", e.target.value)}
            className="max-w-xs"
          >
            <option value="">Not recorded</option>
            {RAW_CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {RAW_CONDITION_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <Field id={`${id}-gradingCompany`} label="Grading company" error={errorFor("gradingCompany")}>
            <Select value={values.gradingCompany} onChange={(e) => set("gradingCompany", e.target.value)}>
              <option value="">Choose…</option>
              {GRADING_COMPANIES.map((c) => (
                <option key={c} value={c}>
                  {GRADING_COMPANY_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          <Field id={`${id}-grade`} label="Grade" hint="1 to 10, e.g. 9 or 9.5" error={errorFor("grade")}>
            <Input
              type="number"
              inputMode="decimal"
              min={1}
              max={10}
              step={0.5}
              value={values.grade}
              onChange={(e) => set("grade", e.target.value)}
            />
          </Field>
          {text("certificateNumber", "Certificate number (optional)")}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-4">
        <Field id={`${id}-quantity`} label="Quantity" error={errorFor("quantity")}>
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            value={values.quantity}
            onChange={(e) => set("quantity", e.target.value)}
          />
        </Field>
        {costHidden ? (
          <p className="text-sm text-muted-foreground sm:col-span-2">
            The purchase price is kept as it was entered; only people allowed to see costs can see it.
          </p>
        ) : (
          <>
            <Field
              id={`${id}-purchasePrice`}
              label="Purchase price (optional)"
              error={errorFor("purchasePrice")}
            >
              <Input
                inputMode="decimal"
                value={values.purchasePrice}
                onChange={(e) => set("purchasePrice", e.target.value)}
              />
            </Field>
            <Field id={`${id}-purchaseCurrency`} label="Currency" error={errorFor("purchaseCurrency")}>
              <Select
                value={values.purchaseCurrency}
                onChange={(e) => set("purchaseCurrency", e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Field id={`${id}-purchaseDate`} label="Purchase date (optional)" error={errorFor("purchaseDate")}>
          <Input
            type="date"
            value={values.purchaseDate}
            onChange={(e) => set("purchaseDate", e.target.value)}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {text("boughtFrom", "Bought from (optional)", { placeholder: "Website, shop or seller" })}
        {text("storageLocation", "Where it is kept (optional)", { placeholder: "e.g. Binder A, page 3" })}
      </div>
      <Field id={`${id}-notes`} label="Notes (optional)" error={errorFor("notes")}>
        <Textarea value={values.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
      </Field>

      {error !== null && error !== undefined && !fieldErrorShown && <ErrorAlert error={error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>
          {pending ? "Saving…" : submitLabel}
        </Button>
        {onCancel !== undefined && (
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
